import React, { useState } from "react";

import styled from "styled-components/macro";

import Notice from "Payments/Shared/Notice";
import { getCardTypePretty } from "Payments/Shared/getCardTypePretty";
import { shouldShowNeedsUpdating } from "Payments/Shared/shouldShowNeedsUpdating";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import FnrPaymentMethodIcon from "UI/FnrPaymentMethodIcon";

import {
  editModalPanels,
  useRecurringGifts,
} from "../../RecurringGiftsContext";

export default function SelectedPaymentMethod() {
  const { state, dispatch } = useRecurringGifts();
  const { editModal } = state;
  const paymentMethod = editModal.giftWithEdits.paymentMethod.ID
    ? editModal.giftWithEdits.paymentMethod
    : editModal.currentGift.paymentMethod;
  const { nickname, last4, cardType } = paymentMethod;
  const cardTypePretty = getCardTypePretty(cardType);
  const [isHovering, setIsHovering] = useState(false);

  const { isSubmitting } = state.editModal;

  const showUpdateNotice = shouldShowNeedsUpdating(paymentMethod);

  const hoverStart = () => {
    if (!isSubmitting) setIsHovering(true);
  };
  const hoverEnd = () => setIsHovering(false);

  const handleClick = () => {
    if (isSubmitting) return;
    dispatch({
      type: "setCurrentPanel",
      payload: editModalPanels.paymentMethods,
    });
  };

  return (
    <Wrapper
      role="button"
      onClick={handleClick}
      onHoverStart={hoverStart}
      onHoverEnd={hoverEnd}
      whileTap={!isSubmitting && { scale: 0.96 }}
      disabled={isSubmitting}
    >
      <IconWrapper>
        <FnrPaymentMethodIcon cardType={cardType} />
      </IconWrapper>

      <MethodContent>
        <CardInfo animate={{ scale: isHovering ? 1.06 : 1 }}>
          <CardLine1>{nickname || cardTypePretty}</CardLine1>

          {showUpdateNotice && <UpdateNotice>Needs updating</UpdateNotice>}

          <CardLine2>
            <Dots>••••</Dots>
            {last4}
          </CardLine2>
        </CardInfo>
        <MoreArrow
          animate={{ scale: isHovering ? 1.06 : 1, x: isHovering ? 4 : 0 }}
          className={classNames({ isHovering })}
        >
          <FnrIcon fas fa-chevron-right />
        </MoreArrow>
      </MethodContent>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  flex-grow: 1;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  display: flex;
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  flex-grow: 0;
  flex-shrink: 0;
  background: white;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: #fff;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 2px;
  position: relative;
  overflow: hidden;
`;

const MethodContent = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  margin-left: 14px;
`;

const CardInfo = styled(motion.div)`
  margin-right: 14px;
  transform-origin: 0 50%;
`;

const CardLine1 = styled.div`
  font-size: 18px;
  font-weight: 400;

  @media (max-width: 360px) {
    font-size: 15px;
  }
`;

const CardLine2 = styled.div`
  color: #5b5b5b;
  font-size: 16px;
  margin-top: 2px;
  display: flex;
  align-items: center;
`;

const Dots = styled.div`
  font-size: 21px;
  line-height: 21px;
  margin-right: 4px;
`;

const MoreArrow = styled(motion.div)`
  font-size: 24px;
  color: #828c8d;
  margin-right: 4px;
  transition: color 0.2s;

  &.isHovering {
    color: var(--fundColor);
  }
`;

const UpdateNotice = styled(Notice)`
  width: max-content;
  border-width: 1px;
  font-size: 12px;
  padding-block: 2px;
  border-radius: 6px;
  margin-top: 2px;
`;
